import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { formatarDia } from '@common/i18n/formatosData';
import { Alert, FormField, Input, RadioGroup, Select, SelectItem } from '@ntconsult/aluma-react';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useAplicarCep, useConsultaCep } from '../hooks';
import type { EnderecoOficialApresentado } from '../types/Formulario';
import { AvisoCep } from './AvisoCep';
import { CartaoPasso } from './CartaoPasso';
import {
  BRASIL,
  entregaDoCadastro,
  instantaneoEntrega,
  mascararCep,
  mascararCepDoPais,
  UFS,
  type Valores,
} from './formulario.utils';
import { OpcaoCartao } from './OpcaoCartao';
import { type CtxPasso, TITULO_PASSO } from './passo.types';
import { cadastroEmLinhas } from './ResumoResposta';

type Faltante = EnderecoOficialApresentado['faltantes'][number];

/** Faltante do cadastro (spec 18) → campo da entrega e rótulo dele. */
const CAMPO_DO_FALTANTE = {
  cep: ['cep', 'cep'],
  endereco: ['logradouro', 'logradouro'],
  numero: ['numero', 'numero'],
  distrito: ['bairro', 'bairro'],
  cidade: ['cidade', 'cidade'],
  uf: ['uf', 'uf'],
} as const satisfies Record<Faltante, readonly [keyof Valores['entrega'], string]>;

/** R7: o que o cadastro tem, em linhas, com cada pedaço que falta como um espaço vazio destacado. */
function EnderecoComBuracos({ cadastro }: { cadastro: EnderecoOficialApresentado }) {
  const { t } = useTranslation('formulario');
  const falta = new Set(cadastro.faltantes);
  const parte = (campo: Faltante, valor: string | null) =>
    falta.has(campo) ? (
      <span key={campo}>
        <span
          aria-hidden
          data-faltante={campo}
          className="inline-block h-5 min-w-16 rounded-sm bg-warning/20 align-middle"
        />
        <span className="sr-only">{t(`campos.${CAMPO_DO_FALTANTE[campo][1]}`)}</span>
      </span>
    ) : (
      valor && <span key={campo}>{valor}</span>
    );
  const cep = cadastro.cep ? mascararCep(cadastro.cep) : null;
  // Como em `linhasEndereco`: "rua, número" e "cidade – UF".
  const linhas = [
    { sep: ',', partes: [parte('endereco', cadastro.endereco), parte('numero', cadastro.numero)] },
    { sep: ',', partes: [cadastro.complemento && <span key="c">{cadastro.complemento}</span>] },
    { sep: ',', partes: [parte('distrito', cadastro.distrito)] },
    { sep: '–', partes: [parte('cidade', cadastro.cidadeNome), parte('uf', cadastro.estadoSigla)] },
    { sep: ',', partes: [parte('cep', cep)] },
  ]
    .map((l) => ({ ...l, partes: l.partes.filter(Boolean) }))
    .filter((l) => l.partes.length > 0);
  return (
    <span className="flex flex-col text-sm text-foreground-muted">
      {linhas.map((l, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: linhas fixas, sem reordenação
        <span key={i} className="flex flex-wrap items-center">
          {l.partes.map((x, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: idem
            <span key={j} className="inline-flex items-center">
              {j > 0 && <span className={l.sep === '–' ? 'mx-1' : 'mr-1'}>{l.sep}</span>}
              {x}
            </span>
          ))}
        </span>
      ))}
    </span>
  );
}

const erroRadio = 'text-sm text-danger';

/** Pergunta 3 do roteiro e o aviso de entrega; as opções da R7. */
export function PassoEntrega({ ctx }: { ctx: CtxPasso }) {
  const { t } = useTranslation(['formulario', 'erros']);
  const { form, cadastro, participacao, escolher, avancar, revalidar, paises } = ctx;
  const { campanha } = participacao;
  const { control, register, setValue, getValues, setError, setFocus, watch } = form;
  const e = form.formState.errors;
  const origem = watch('origem');
  const emOutro = origem === 'outro' || origem === 'completar';
  const cadastroOk = !!cadastro?.completo;
  const podeCompletar = !!cadastro && !cadastro.exterior && !cadastroOk;
  const faltantes = cadastro?.faltantes ?? [];
  const datasEntrega =
    campanha.entregaPrevistaInicio && campanha.entregaPrevistaFim
      ? ([campanha.entregaPrevistaInicio, campanha.entregaPrevistaFim] as const)
      : null;
  const paisesEntrega = (paises.data ?? []).filter((p) => String(p.id) !== BRASIL);

  // Registro com a revalidação a cada mudança de um campo em erro (R4, R7).
  const reg = (
    campo: `entrega.${keyof Valores['entrega']}` | `exterior.${keyof Valores['exterior']}`,
  ) => register(campo, { onChange: () => revalidar(campo) });

  // Busca de CEP da entrega: só depois de a pessoa mexer no CEP (o salvo não dispara nada).
  const [cepTocado, setCepTocado] = useState(false);
  const consulta = useConsultaCep({ cep: watch('entrega.cep'), habilitado: emOutro && cepTocado });
  useAplicarCep(consulta.endereco, getValues('entrega'), (d, escrever) => {
    const v = getValues('entrega');
    const set = (campo: 'logradouro' | 'bairro' | 'cidade' | 'uf') => (x: string) => {
      setValue(`entrega.${campo}`, x);
      revalidar(`entrega.${campo}`);
    };
    escrever('logradouro', v.logradouro, d.logradouro ?? '', set('logradouro'));
    escrever('bairro', v.bairro, d.bairro ?? '', set('bairro'));
    escrever('cidade', v.cidade, d.cidade, set('cidade'));
    escrever('uf', v.uf, (UFS as readonly string[]).includes(d.uf) ? d.uf : '', set('uf'));
    setFocus('entrega.numero');
  });

  /** R7: os campos já preenchidos com o cadastro, os que faltam em erro e o foco no primeiro deles. */
  function completar() {
    if (!cadastro) return;
    const dados = entregaDoCadastro(cadastro);
    const atuais = getValues('entrega');
    for (const k of Object.keys(dados) as (keyof typeof dados)[])
      if (!atuais[k].trim()) setValue(`entrega.${k}`, dados[k]);
    const campos = faltantes.map((f) => `entrega.${CAMPO_DO_FALTANTE[f][0]}` as const);
    for (const c of campos)
      if (!getValues(c).trim())
        setError(c, { type: 'required', message: t('formulario:validacao.obrigatorio') });
    // Espera o bloco montar antes de focar.
    setTimeout(() => campos[0] && setFocus(campos[0]), 0);
  }

  function selecionar(x: string) {
    escolher('origem', x as Valores['origem']);
    if (x === 'completar') completar();
  }

  const escolha = (x: string) => {
    selecionar(x);
    if (x === 'cadastro') avancar();
  };

  function continuar() {
    // A entrega mudou depois de copiada para o oficial: o "mesmo" pergunta de novo (R8).
    if (getValues('mesmo') === 'sim' && ctx.copia.current !== instantaneoEntrega(getValues()))
      escolher('mesmo', '');
    void ctx.continuar();
  }

  return (
    <CartaoPasso
      titulo={t('formulario:entrega.titulo')}
      // Aviso do roteiro entre as perguntas 3 e 4 (spec 21 R4); no exterior, o do exterior (spec 11 R5).
      subtitulo={
        <p className="text-sm text-foreground-muted">
          <Trans
            ns="formulario"
            i18nKey={
              origem === 'exterior'
                ? 'entrega.avisoExterior'
                : datasEntrega
                  ? 'entrega.avisoDatas'
                  : campanha.janelaEnvio
                    ? 'entrega.aviso'
                    : 'entrega.avisoSemJanela'
            }
            // `values` é tipado pela união das variáveis de todas as chaves do namespace.
            values={
              {
                janela: campanha.janelaEnvio ?? '',
                inicio: datasEntrega ? formatarDia(datasEntrega[0]) : '',
                fim: datasEntrega ? formatarDia(datasEntrega[1]) : '',
              } as never
            }
            components={{ strong: <strong className="font-semibold text-foreground" /> }}
          />
        </p>
      }
      onVoltar={ctx.onVoltar}
      onContinuar={ctx.respondido ? continuar : undefined}
    >
      <RadioGroup
        name="origem"
        aria-labelledby={TITULO_PASSO}
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        value={origem}
        onValueChange={selecionar}
      >
        {cadastro && cadastroOk && (
          <OpcaoCartao
            value="cadastro"
            label={t('formulario:entrega.cadastro')}
            onEscolher={escolha}
          >
            <span className="flex flex-col text-sm text-foreground-muted">
              {cadastroEmLinhas(cadastro).map((l) => (
                <span key={l}>{l}</span>
              ))}
            </span>
            {/* Spec 18 R6a: dentro do cartão, antes da escolha (o avanço automático pularia o aviso). */}
            {cadastro.separadoAutomaticamente && (
              <span className="mt-2 flex flex-col gap-1 text-sm">
                <span className="font-medium text-foreground">
                  {t('formulario:entrega.separado')}
                </span>
                <span>
                  {t('formulario:campos.logradouro')}: {cadastro.endereco}
                </span>
                <span>
                  {t('formulario:campos.numero')}: {cadastro.numero}
                </span>
                {cadastro.complemento && (
                  <span>
                    {t('formulario:campos.complemento')}: {cadastro.complemento}
                  </span>
                )}
                <span className="text-foreground-muted">
                  {t('formulario:entrega.enderecoOriginal')} {cadastro.enderecoOriginal}
                </span>
                <button
                  type="button"
                  className="self-start text-brand-primary underline"
                  onClick={(ev) => {
                    // Dentro do `label`: sem isto o clique marcaria "neste endereço" e avançaria.
                    ev.preventDefault();
                    ev.stopPropagation();
                    selecionar('completar');
                  }}
                >
                  {t('formulario:entrega.corrigir')}
                </button>
              </span>
            )}
          </OpcaoCartao>
        )}
        {podeCompletar && (
          <OpcaoCartao
            value="completar"
            label={t('formulario:entrega.completar')}
            onEscolher={escolha}
          >
            <EnderecoComBuracos cadastro={cadastro} />
          </OpcaoCartao>
        )}
        <OpcaoCartao value="outro" label={t('formulario:entrega.outro')} onEscolher={escolha} />
        <OpcaoCartao value="exterior" label={t('formulario:entrega.exterior')} onEscolher={escolha}>
          <span className="text-sm text-foreground-muted">
            {t('formulario:entrega.exteriorDescricao')}
          </span>
          {cadastro?.exterior && (
            <span className="text-sm text-foreground-muted">
              {t('formulario:entrega.cadastroExterior')}
            </span>
          )}
        </OpcaoCartao>
      </RadioGroup>
      {e.origem && (
        <p role="alert" className={erroRadio}>
          {e.origem.message}
        </p>
      )}

      {origem === 'completar' &&
        !cadastro?.separadoAutomaticamente &&
        faltantes.includes('numero') &&
        /\d/.test(cadastro?.endereco ?? '') && (
          <Alert variant="info" title={t('formulario:entrega.numeroPodeEstarJunto')} />
        )}

      {emOutro && (
        <div className="flex flex-col gap-4">
          <h3 className="text-sm font-semibold text-foreground">
            {t('formulario:entrega.outroTitulo')}
          </h3>
          {/* Em linhas, na ordem da spec 18 R10: CEP, Logradouro e Número; Complemento e Bairro. */}
          <div className="grid gap-4 sm:grid-cols-[9rem_1fr_7rem]">
            <Controller
              control={control}
              name="entrega.cep"
              render={({ field }) => (
                <FormField
                  label={t('formulario:campos.cep')}
                  error={e.entrega?.cep?.message}
                  required
                >
                  <Input
                    inputMode="numeric"
                    autoComplete="postal-code"
                    maxLength={9}
                    value={field.value}
                    onChange={(ev) => {
                      setCepTocado(true);
                      field.onChange(mascararCepDoPais(ev.target.value));
                      revalidar('entrega.cep');
                    }}
                    onBlur={field.onBlur}
                    ref={field.ref}
                  />
                </FormField>
              )}
            />
            <FormField
              label={t('formulario:campos.logradouro')}
              error={e.entrega?.logradouro?.message}
              required
            >
              <Input
                maxLength={300}
                autoComplete="address-line1"
                placeholder={t('formulario:campos.logradouroExemplo')}
                {...reg('entrega.logradouro')}
              />
            </FormField>
            <FormField
              label={t('formulario:campos.numero')}
              error={e.entrega?.numero?.message}
              required
            >
              <Input maxLength={10} {...reg('entrega.numero')} />
            </FormField>
          </div>
          <AvisoCep consulta={consulta} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FormField
              label={t('formulario:campos.complemento')}
              error={e.entrega?.complemento?.message}
            >
              <Input
                maxLength={200}
                placeholder={t('formulario:campos.complementoExemplo')}
                {...reg('entrega.complemento')}
              />
            </FormField>
            <FormField
              label={t('formulario:campos.bairro')}
              error={e.entrega?.bairro?.message}
              required
            >
              <Input maxLength={100} {...reg('entrega.bairro')} />
            </FormField>
            <FormField
              label={t('formulario:campos.cidade')}
              error={e.entrega?.cidade?.message}
              required
            >
              <Input maxLength={255} autoComplete="address-level2" {...reg('entrega.cidade')} />
            </FormField>
            <Controller
              control={control}
              name="entrega.uf"
              render={({ field }) => (
                <FormField
                  label={t('formulario:campos.uf')}
                  error={e.entrega?.uf?.message}
                  required
                >
                  <Select
                    value={field.value}
                    onValueChange={(x) => {
                      field.onChange(x);
                      revalidar('entrega.uf');
                    }}
                    placeholder={t('formulario:selecione')}
                  >
                    {UFS.map((u) => (
                      <SelectItem key={u} value={u}>
                        {u}
                      </SelectItem>
                    ))}
                  </Select>
                </FormField>
              )}
            />
          </div>
        </div>
      )}

      {origem === 'exterior' && (
        <div className="flex flex-col gap-4">
          <h3 className="font-medium text-foreground">{t('formulario:entrega.exteriorTitulo')}</h3>
          {/* País e endereço; cidade, região e código postal; telefone com a dica, sozinho na linha. */}
          <div className="grid gap-4 sm:grid-cols-3">
            {paises.isError ? (
              <div className="sm:col-span-3">
                <Alert variant="danger" title={t(`erros:${mensagemDeErro(paises.error)}`)} />
              </div>
            ) : (
              <Controller
                control={control}
                name="exterior.paisId"
                render={({ field }) => (
                  <FormField
                    label={t('formulario:campos.pais')}
                    error={e.exterior?.paisId?.message}
                    required
                  >
                    <Select
                      value={field.value}
                      placeholder={t('formulario:selecione')}
                      onValueChange={(x) => {
                        field.onChange(x);
                        const nome = paisesEntrega.find((p) => String(p.id) === x)?.nome;
                        setValue('exterior.pais', nome ?? '');
                        revalidar('exterior.paisId');
                      }}
                    >
                      {paisesEntrega.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.nome}
                        </SelectItem>
                      ))}
                    </Select>
                  </FormField>
                )}
              />
            )}
            <div className="sm:col-span-2">
              <FormField
                label={t('formulario:campos.enderecoLivre')}
                error={e.exterior?.endereco?.message}
                required
              >
                <Input maxLength={500} autoComplete="address-line1" {...reg('exterior.endereco')} />
              </FormField>
            </div>
            <FormField
              label={t('formulario:campos.cidade')}
              error={e.exterior?.cidade?.message}
              required
            >
              <Input maxLength={255} autoComplete="address-level2" {...reg('exterior.cidade')} />
            </FormField>
            <FormField label={t('formulario:campos.regiao')} error={e.exterior?.regiao?.message}>
              <Input maxLength={100} autoComplete="address-level1" {...reg('exterior.regiao')} />
            </FormField>
            <FormField
              label={t('formulario:campos.codigoPostal')}
              error={e.exterior?.codigoPostal?.message}
            >
              <Input maxLength={30} autoComplete="postal-code" {...reg('exterior.codigoPostal')} />
            </FormField>
            <FormField
              label={t('formulario:campos.telefone')}
              hint={t('formulario:entrega.telefoneHint')}
              error={e.exterior?.telefone?.message}
              required
            >
              <Input type="tel" maxLength={30} autoComplete="tel" {...reg('exterior.telefone')} />
            </FormField>
          </div>
        </div>
      )}
    </CartaoPasso>
  );
}
